/**
 * /tools/superhero?clientId=X — STP-025 / #18.
 *
 * Reworked into THREE identity aspects (Energy / Work / Love). For each:
 * the hero the client admires, the qualities they admire (1..5 → virtue
 * pool, axis-tagged), and the heroic name the client would give themselves
 * (→ identity pool, axis-tagged). Client-filled, then locked to coach-only
 * edits (the save warning says so).
 *
 * Persists via the generic tools route → versioned `client_documents` row
 * w/ `doc_type='superhero'`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';
import { AxisIcon } from '@/components/axis-icon';

const MAX_QUALITIES = 5;
const AXES = ['energy', 'work', 'love'] as const;
type Axis = (typeof AXES)[number];

interface AspectForm {
  admiredHero: string;
  qualities: string[];
  selfName: string;
}
type FormState = Record<Axis, AspectForm>;

const emptyAspect = (): AspectForm => ({ admiredHero: '', qualities: [''], selfName: '' });
const defaultForm = (): FormState => ({ energy: emptyAspect(), work: emptyAspect(), love: emptyAspect() });

function hydrateAspect(raw: unknown): AspectForm {
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const qualities = Array.isArray(o.qualities)
      ? o.qualities.filter((x): x is string => typeof x === 'string').slice(0, MAX_QUALITIES)
      : [];
    return {
      admiredHero: typeof o.admiredHero === 'string' ? o.admiredHero : '',
      qualities: qualities.length > 0 ? qualities : [''],
      selfName: typeof o.selfName === 'string' ? o.selfName : '',
    };
  }
  return emptyAspect();
}

function trimmedQualities(aspect: AspectForm): string[] {
  return aspect.qualities.map((q) => q.trim()).filter((q) => q.length > 0);
}

function aspectComplete(aspect: AspectForm): boolean {
  return (
    aspect.admiredHero.trim().length > 0 &&
    aspect.selfName.trim().length > 0 &&
    trimmedQualities(aspect).length >= 1
  );
}

export function SuperheroPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const { isPristine, markSaved } = usePristine<FormState>(defaultForm());

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'superhero');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          // Only hydrate the new three-aspect shape; legacy single-hero
          // completions just start fresh (the tool is re-filled).
          if ('energy' in p || 'work' in p || 'love' in p) {
            const hydratedForm: FormState = {
              energy: hydrateAspect(p.energy),
              work: hydrateAspect(p.work),
              love: hydrateAspect(p.love),
            };
            setForm(hydratedForm);
            markSaved(hydratedForm);
          }
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit = useMemo(
    () =>
      !submitting &&
      hydrated &&
      !isPristine(form) &&
      AXES.every((axis) => aspectComplete(form[axis])),
    [submitting, hydrated, isPristine, form],
  );

  const setAspect = (axis: Axis, next: AspectForm): void => {
    setForm((prev) => ({ ...prev, [axis]: next }));
  };
  const updateField = (axis: Axis, field: 'admiredHero' | 'selfName', value: string): void => {
    setAspect(axis, { ...form[axis], [field]: value });
  };
  const updateQuality = (axis: Axis, index: number, value: string): void => {
    const next = [...form[axis].qualities];
    next[index] = value;
    setAspect(axis, { ...form[axis], qualities: next });
  };
  const addQuality = (axis: Axis): void => {
    if (form[axis].qualities.length >= MAX_QUALITIES) return;
    setAspect(axis, { ...form[axis], qualities: [...form[axis].qualities, ''] });
  };
  const removeQuality = (axis: Axis, index: number): void => {
    if (form[axis].qualities.length <= 1) return;
    setAspect(axis, { ...form[axis], qualities: form[axis].qualities.filter((_, i) => i !== index) });
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.superhero.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.superhero.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      energy: {
        admiredHero: form.energy.admiredHero.trim(),
        qualities: trimmedQualities(form.energy),
        selfName: form.energy.selfName.trim(),
      },
      work: {
        admiredHero: form.work.admiredHero.trim(),
        qualities: trimmedQualities(form.work),
        selfName: form.work.selfName.trim(),
      },
      love: {
        admiredHero: form.love.admiredHero.trim(),
        qualities: trimmedQualities(form.love),
        selfName: form.love.selfName.trim(),
      },
    };
    try {
      await api.submitToolCompletion(accessToken, clientId, 'superhero', { payload });
      markSaved(form);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.superhero.errors.generic'));
      } else {
        setSubmitError(t('tools.superhero.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="superhero-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.superhero.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.superhero.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.superhero.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">{t('tools.superhero.title')}</h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.superhero.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {AXES.map((axis) => (
          <AspectCard
            key={axis}
            axis={axis}
            aspect={form[axis]}
            onField={(field, value) => updateField(axis, field, value)}
            onQuality={(idx, value) => updateQuality(axis, idx, value)}
            onAddQuality={() => addQuality(axis)}
            onRemoveQuality={(idx) => removeQuality(axis, idx)}
          />
        ))}

        {/* #18 — coach-only-after-save warning. */}
        <p
          className="rounded-md border border-semantic-warning/40 bg-semantic-warning/10 p-s4 text-sm text-ink"
          data-testid="superhero-save-warning"
        >
          {t('tools.superhero.saveWarning')}
        </p>

        {submitError && (
          <p role="alert" className="form-error" data-testid="superhero-submit-error">
            {submitError}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.superhero.submitting') : t('tools.superhero.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

function AspectCard({
  axis,
  aspect,
  onField,
  onQuality,
  onAddQuality,
  onRemoveQuality,
}: {
  axis: Axis;
  aspect: AspectForm;
  onField: (field: 'admiredHero' | 'selfName', value: string) => void;
  onQuality: (index: number, value: string) => void;
  onAddQuality: () => void;
  onRemoveQuality: (index: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const axisLabel = t(`common.axis.${axis}`);
  return (
    <article
      className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s4"
      data-testid={`superhero-aspect-${axis}`}
    >
      <header className="flex items-center gap-s2">
        <AxisIcon axis={axis} size={20} ariaLabel={axisLabel} />
        <h2 className="font-display text-display-sm text-ink">
          {t(`tools.superhero.aspects.${axis}`)}
        </h2>
      </header>

      <div>
        <label htmlFor={`superhero-${axis}-hero`} className="form-label">
          {t('tools.superhero.fields.admiredHero.title')}
        </label>
        <p className="text-sm text-gray-2 italic mb-s2">
          {t('tools.superhero.fields.admiredHero.prompt', { axis: axisLabel })}
        </p>
        <input
          id={`superhero-${axis}-hero`}
          type="text"
          value={aspect.admiredHero}
          onChange={(e) => onField('admiredHero', e.target.value)}
          placeholder={t('tools.superhero.fields.admiredHero.placeholder')}
          className="input-field"
          data-testid={`superhero-${axis}-hero`}
        />
      </div>

      <div>
        <span className="form-label">{t('tools.superhero.fields.qualities.title')}</span>
        <p className="text-sm text-gray-2 italic mb-s2">
          {t('tools.superhero.fields.qualities.prompt')}
        </p>
        <div className="flex flex-col gap-s2" data-testid={`superhero-${axis}-qualities`}>
          {aspect.qualities.map((q, idx) => (
            <div key={idx} className="flex gap-s2">
              <input
                type="text"
                value={q}
                onChange={(e) => onQuality(idx, e.target.value)}
                placeholder={t('tools.superhero.fields.qualities.placeholder')}
                className="input-field flex-1"
                data-testid={`superhero-${axis}-quality-${idx}`}
                aria-label={`${t('tools.superhero.fields.qualities.title')} ${idx + 1}`}
              />
              {aspect.qualities.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemoveQuality(idx)}
                  className="text-sm text-gray-1 hover:text-flame-ember px-s2"
                  data-testid={`superhero-${axis}-quality-remove-${idx}`}
                  aria-label={t('tools.superhero.fields.qualities.removeLabel')}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {aspect.qualities.length < MAX_QUALITIES && (
            <button
              type="button"
              onClick={onAddQuality}
              className="text-sm text-flame-deep hover:text-flame self-start"
              data-testid={`superhero-${axis}-quality-add`}
            >
              + {t('tools.superhero.fields.qualities.addLabel')}
            </button>
          )}
        </div>
      </div>

      <div>
        <label htmlFor={`superhero-${axis}-name`} className="form-label">
          {t('tools.superhero.fields.selfName.title')}
        </label>
        <p className="text-sm text-gray-2 italic mb-s2">
          {t('tools.superhero.fields.selfName.prompt', { axis: axisLabel })}
        </p>
        <input
          id={`superhero-${axis}-name`}
          type="text"
          value={aspect.selfName}
          onChange={(e) => onField('selfName', e.target.value)}
          placeholder={t('tools.superhero.fields.selfName.placeholder')}
          className="input-field"
          data-testid={`superhero-${axis}-name`}
        />
      </div>
    </article>
  );
}
